import React from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

export type TripResultStatus =
	| 'Scheduled'
	| 'Closed'
	| 'Disabled'
	| 'Unavailable'
	| 'WalkOnNotOffered'
	| 'SoldOut'
	| 'NoTripFound'
	| 'Departed';

interface TripResultItemProps {
	date: Date;
	time?: string;
	status: TripResultStatus;
	errorMessage?: string;
	errorContent?: React.ReactNode;
	/**
	 * A neutral line under the row, for something worth saying about a trip that is not a failure —
	 * today, which booking types a still-bookable trip is closed to.
	 */
	note?: string;
}

export default function TripResultItem({
	date,
	time,
	status,
	errorMessage,
	errorContent,
	note,
}: TripResultItemProps) {
	const { formatMessage } = useIntl();
	const statusConfig = {
		Scheduled: {
			icon: 'check_circle',
			color: 'text-green-700',
			bgColor: 'bg-green-50',
			label: 'Scheduled',
		},
		Closed: {
			icon: 'block',
			color: 'text-orange-700',
			bgColor: 'bg-orange-50',
			label: 'Closed',
		},
		// Only ever reached from the reservation step's "Some trips are unavailable" list, where the
		// trip was cancelled after the user picked it. The bulk search never produces this status:
		// a cancelled trip is filtered out of the schedule query, so `Review trips` shows the date as
		// Not Scheduled instead. Keyed on the entity's Disabled flag, labelled in the product's word.
		Disabled: {
			icon: 'do_not_disturb_on',
			color: 'text-gray-700',
			bgColor: 'bg-gray-100',
			label: 'Cancelled',
		},
		Unavailable: {
			icon: 'visibility_off',
			color: 'text-amber-700',
			bgColor: 'bg-amber-50',
			label: 'Unavailable',
		},
		// Distinct from SoldOut on purpose: nothing ran out here, the vessel simply isn't sold for
		// walk-on fares. Labelling it "Sold Out" would send the customer away waiting for space that
		// was never the constraint.
		WalkOnNotOffered: {
			icon: 'block',
			color: 'text-orange-700',
			bgColor: 'bg-orange-50',
			label: 'No walk-ons',
		},
		SoldOut: {
			icon: 'cancel',
			color: 'text-red-700',
			bgColor: 'bg-red-50',
			label: 'Sold Out',
		},
		NoTripFound: {
			icon: 'warning',
			color: 'text-amber-700',
			bgColor: 'bg-amber-50',
			label: 'Not Scheduled',
		},
		Departed: {
			icon: 'schedule',
			color: 'text-gray-700',
			bgColor: 'bg-gray-100',
			label: formatMessage({ id: 'general__departed__label', defaultMessage: 'Departed' }),
		},
	};

	const config = statusConfig[status];

	return (
		<div className={classNames('flex flex-col p-3 rounded-md mb-2 border border-gray-100 bg-white')}>
			<div className="flex items-center justify-between w-full">
				<div className="flex items-center gap-4">
					<div className="flex flex-col">
						{/* Weekday included on purpose: a bulk booking is a repeating pattern, and the
						    thing to check down this list is that every row landed on the day the pattern
						    asked for. Reading that off a bare date means counting. */}
						<span className="font-semibold text-gray-900">
							{dayjs(date).format('dddd MMM D, YYYY')}
						</span>
						{time && <span className="text-sm text-gray-500">{time}</span>}
					</div>
				</div>
				<div
					className={classNames(
						'flex items-center gap-2 font-medium px-3 py-1 rounded-full text-sm',
						config.bgColor,
						config.color,
					)}
				>
					<Icon name={config.icon} useMaterialIcons classname="text-lg" />
					<span>{config.label}</span>
				</div>
			</div>
			{(errorMessage || errorContent) && (
				<div className="flex items-start gap-2 pt-2 mt-2 text-sm font-medium">
					{/* <Icon name="error" useMaterialIcons classname="text-lg mt-0.5" /> */}
					<div className="flex-1">{errorContent || <span>{errorMessage}</span>}</div>
				</div>
			)}
			{note && !errorMessage && !errorContent && (
				<div className="pt-2 mt-2 text-sm text-gray-500">{note}</div>
			)}
		</div>
	);
}
